import { v4 as uuidv4 } from "uuid";
import { UserProfile } from "@/types/user";
import { ChatMessage, ChatPreview } from "../types";
import { useToast } from "@/hooks/use-toast";
import {
  updateUserAvatar,
} from "../services";
import { sendChatMessage, fetchChatMessages } from "../services/chatMessageService";

export function useChatActions(
  user: UserProfile | null,
  chatPreviews: ChatPreview[],
  setChatPreviews: React.Dispatch<React.SetStateAction<ChatPreview[]>>,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setRecipient: React.Dispatch<React.SetStateAction<UserProfile | null>>,
  selectedChatId: string | undefined,
  setSelectedChatId: React.Dispatch<React.SetStateAction<string | undefined>>
) {
  const { toast } = useToast();

  // Handle chat selection
  const handleSelectChat = async (chatId: string) => {
    setSelectedChatId(chatId);
    // Find the selected chat preview
    const selectedChat = chatPreviews.find((chat) => chat.id === chatId);
    if (selectedChat) {
      setRecipient(selectedChat.user);

      // Special handling for welcome-chat (client-side placeholder)
      if (chatId === "welcome-chat") {
        // Show welcome message locally without API call
        setMessages([
          {
            id: "welcome-message-1",
            content: "Welcome to Step! 👋",
            timestamp: new Date(),
            senderId: "welcome-bot",
            senderName: "Welcome Bot",
            senderAvatar: "https://api.dicebear.com/6.x/bottts/svg?seed=welcome",
          },
          {
            id: "welcome-message-2",
            content: "You're all set up and ready to go. To start chatting with your team, invite members from the Team page.",
            timestamp: new Date(),
            senderId: "welcome-bot",
            senderName: "Welcome Bot",
            senderAvatar: "https://api.dicebear.com/6.x/bottts/svg?seed=welcome",
          },
        ]);
        return;
      }

      if (user) {
        try {
          const messages = await fetchChatMessages(chatId);
          setMessages(messages);
        } catch (error) {
          console.error("Error loading messages:", error);
          toast({
            title: "Error",
            description: "Failed to load messages",
            variant: "destructive",
          });
        }
      }

      // Mark as read if unread
      if (selectedChat.unread) {
        setChatPreviews((prevChats) =>
          prevChats.map((chat) =>
            chat.id === chatId ? { ...chat, unread: false } : chat
          )
        );
      }
    }
  };

  // Handle sending a new message
  const handleSendMessage = async (content: string) => {
    if (!user || !selectedChatId) return;

    // Prevent sending to welcome-chat placeholder
    if (selectedChatId === "welcome-chat") {
      toast({
        title: "Cannot send messages",
        description: "Invite team members to start chatting!",
        variant: "default",
      });
      return;
    }

    const selectedChat = chatPreviews.find(
      (chat) => chat.id === selectedChatId
    );
    if (!selectedChat) return;

    const recipient = selectedChat.user;
    const newMessage: ChatMessage = {
      id: uuidv4(),
      content,
      timestamp: new Date(),
      senderId: user.id,
      senderName: user.name,
      senderAvatar:
        user.avatarUrl ||
        `https://api.dicebear.com/7.x/personas/svg?seed=${user.name}`,
    };

    try {
      await sendChatMessage(
        selectedChatId,
        user.id,
        recipient.id,
        content
      );

      // Add message to chat
      setMessages((prev) => [...prev, newMessage]);

      // Move chat to top and update preview
      setChatPreviews((prevChats) => {
        const updatedChats = prevChats.map((chat) =>
          chat.id === selectedChatId
            ? {
                ...chat,
                lastMessage: content,
                timestamp: new Date(),
              }
            : chat
        );

        // Find the updated chat
        const updatedChat = updatedChats.find(
          (chat) => chat.id === selectedChatId
        );

        if (updatedChat) {
          // Remove the chat from the array
          const filteredChats = updatedChats.filter(
            (chat) => chat.id !== selectedChatId
          );
          // Add it to the beginning
          return [updatedChat, ...filteredChats];
        }

        return updatedChats;
      });

      // Email notifications are now handled automatically by the backend
      // when messages are sent via the chat API

      // Show toast notification
      toast({
        description: `Message sent to ${recipient.name}`,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  // Handle profile picture change
  const handleProfilePicChange = (userId: string, newAvatarUrl: string) => {
    // Update avatar in chat previews
    setChatPreviews((prevChats) =>
      prevChats.map((chat) => {
        if (chat.user.id === userId) {
          return {
            ...chat,
            user: {
              ...chat.user,
              avatarUrl: newAvatarUrl,
            },
          };
        }
        return chat;
      })
    );

    // Update recipient if it's the selected chat
    const recipient = chatPreviews.find(
      (chat) => chat.id === selectedChatId
    )?.user;
    if (recipient && recipient.id === userId) {
      setRecipient({
        ...recipient,
        avatarUrl: newAvatarUrl,
      });
    }

    // Call API to update avatar
    updateUserAvatar(userId, newAvatarUrl);

    toast({
      title: "Profile picture updated",
      description: "Your changes have been saved successfully",
    });
  };

  return {
    handleSelectChat,
    handleSendMessage,
    handleProfilePicChange,
  };
}
